import { useCallback, useEffect, useMemo, useState } from 'react'
import { pruneSelection } from 'lib/boardSelection'
import type { ListBoardSelection } from './boardTypes'

const NONE: ReadonlySet<string> = new Set()

/**
 * A board's bulk-selection mode. `selection` is undefined while the mode is off, so the
 * board drags and opens as usual. A pick that leaves `visibleIds` leaves the selection,
 * so the bar never acts on somebody nobody can see.
 */
export function useBoardSelection(visibleIds: ReadonlySet<string>) {
  const [selecting, setSelecting] = useState(false)
  const [selected, setSelected] = useState(NONE)

  useEffect(() => setSelected((previous) => pruneSelection(previous, visibleIds)), [visibleIds])

  const clear = useCallback(() => setSelected(NONE), [])
  const exit = useCallback(() => {
    setSelecting(false)
    setSelected(NONE)
  }, [])
  const toggleSelecting = useCallback(() => (selecting ? exit() : setSelecting(true)), [selecting, exit])

  const selection = useMemo<ListBoardSelection | undefined>(
    () => (selecting ? { selected, onChange: setSelected } : undefined),
    [selecting, selected],
  )

  return { selecting, selected, selection, clear, exit, toggleSelecting }
}
